import { FormattedMenuSectionInfo, MenuMappingSubsectionItemResource, TypeName, MenuMappingCustomizationCustomizationOption, ItemModifier } from 'api'
import { ModifierSortUpdate } from 'api/menuMapping/types'
import { getMenuMappingSubsection } from 'api/menuMapping/subsections'
import { 
  bulkUpdateCustomizationOptionsSortPositions,
  getMenuMappingCustomizationCustomizationOptions
} from 'api/menuMapping/customizationOptions'
import { updateMenuMappingItemCustomizationSortPosition } from 'api/menuMapping/itemCustomizations'
import { updateMenuMappingNestedCustomization } from 'api/menuMapping/nestedCustomizations'

export interface DragDropItem {
  id: string
  index: number
  sortPosition?: number
  subsectionItemId?: string
}

export interface ParentItemSortUpdate {
  subsectionItemId: string
  itemId: string
  subsectionId: string
  newSortPosition: number
}

export interface ChildItemSortUpdate {
  childItemId: string
  newSortPosition: number
}

/**
 * Calculate new sort positions for items after a drag-and-drop operation
 * @param items - Array of items in their new order
 * @param subsectionId - The subsection ID containing the items
 * @returns Array of staged sort updates
 */
export const calculateParentItemSortPositions = async (
  items: DragDropItem[],
  subsectionId: string
): Promise<ParentItemSortUpdate[]> => {
  try {
    // Get current subsection data to find subsection item IDs
    const subsectionResponse = await getMenuMappingSubsection(subsectionId, { include: 'subsection_items' })
    
    const subsectionItems = (subsectionResponse.included || []).filter(
      (item): item is MenuMappingSubsectionItemResource => 
        item.type === TypeName.MenuMappingSubsectionItem &&
        item.relationships.subsection.data.id === subsectionId
    )

    // Create a map of item ID to subsection item for quick lookup
    const itemToSubsectionItemMap = new Map<string, MenuMappingSubsectionItemResource>()
    subsectionItems.forEach(subsectionItem => {
      const itemId = subsectionItem.relationships.item.data.id
      itemToSubsectionItemMap.set(itemId, subsectionItem)
    })

    // Calculate new sort positions (1-based indexing)
    const stagedUpdates: ParentItemSortUpdate[] = items.map((item, index) => {
      const subsectionItem = itemToSubsectionItemMap.get(item.id)
      if (!subsectionItem) {
        throw new Error(`Subsection item not found for item ${item.id}`)
      }

      return {
        subsectionItemId: subsectionItem.id,
        itemId: item.id,
        subsectionId,
        newSortPosition: index + 1
      }
    })

    return stagedUpdates
  } catch (error) {
    console.error('Error calculating new sort positions:', error)
    throw error
  }
}

/**
 * Calculate new sort positions for child items after a drag-and-drop operation
 * @param items - Array of child items in their new order
 * @returns Array of child item sort updates
 */
export const calculateChildItemSortPositions = (
  items: FormattedMenuSectionInfo[]
): ChildItemSortUpdate[] => {
  // Calculate new sort positions (1-based indexing)
  const childItemUpdates: ChildItemSortUpdate[] = items.map((item, index) => ({
    childItemId: (item as any).childItemId || item.id, // Use childItemId if available, fallback to item.id
    newSortPosition: index + 1
  }))

  return childItemUpdates
}

/**
 * Stage local reordering of items without making API calls
 * @param items - Array of items in their current order
 * @param draggedItemId - ID of the item being dragged
 * @param newIndex - New index where the item should be placed
 * @returns Array of items in their new order
 */
export const stageLocalReordering = (
  items: FormattedMenuSectionInfo[],
  draggedItemId: string,
  newIndex: number
): FormattedMenuSectionInfo[] => {
  const itemIndex = items.findIndex(item => item.id === draggedItemId)
  if (itemIndex === -1) return items

  const newItems = [...items]
  const [draggedItem] = newItems.splice(itemIndex, 1)
  newItems.splice(newIndex, 0, draggedItem)

  return newItems
}

/**
 * Validate that the new sort positions are valid
 * @param stagedUpdates - Array of staged sort updates
 * @returns boolean indicating if the updates are valid
 */
export const validateParentItemSortPositions = (stagedUpdates: ParentItemSortUpdate[]): boolean => {
  if (stagedUpdates.length === 0) return true

  // Check for duplicate sort positions
  const sortPositions = stagedUpdates.map(update => update.newSortPosition)
  const uniquePositions = new Set(sortPositions)
  
  if (uniquePositions.size !== sortPositions.length) {
    return false
  }

  // Check that positions are sequential starting from 1
  const sortedPositions = sortPositions.sort((a, b) => a - b)
  for (let i = 0; i < sortedPositions.length; i++) {
    if (sortedPositions[i] !== i + 1) {
      return false
    }
  }

  return true
}

/**
 * Validate that the new child item sort positions are valid
 * @param childItemUpdates - Array of child item sort updates
 * @returns boolean indicating if the updates are valid
 */
export const validateChildItemSortPositions = (childItemUpdates: ChildItemSortUpdate[]): boolean => {
  if (childItemUpdates.length === 0) return true

  // Check for duplicate sort positions
  const sortPositions = childItemUpdates.map(update => update.newSortPosition)
  const uniquePositions = new Set(sortPositions)
  
  if (uniquePositions.size !== sortPositions.length) {
    return false
  }

  // Check that positions are sequential starting from 1
  const sortedPositions = sortPositions.sort((a, b) => a - b)
  for (let i = 0; i < sortedPositions.length; i++) {
    if (sortedPositions[i] !== i + 1) {
      return false
    }
  }

  return true
}

/**
 * Calculate new sort positions for modifiers after a drag-and-drop operation
 * @param modifiers - Array of modifiers in their new order
 * @param customizationId - ID of the parent customization group
 * @returns Array of modifier sort updates
 */
export const calculateModifierSortPositions = (
  modifiers: FormattedMenuSectionInfo[],
  customizationId: string
): ModifierSortUpdate[] => {
  // Calculate new sort positions (1-based indexing)
  const modifierUpdates: ModifierSortUpdate[] = modifiers.map((modifier, index) => ({
    modifierId: modifier.id,
    customizationId,
    newSortPosition: index + 1,
    price: modifier.attributes.price || 0
  }))

  return modifierUpdates
}

/**
 * Validate the calculated sort positions for modifiers
 * @param updates - Array of modifier sort updates to validate
 * @returns boolean indicating if the updates are valid
 */
export const validateModifierSortPositions = (updates: ModifierSortUpdate[]): boolean => {
  if (!updates.length) return false

  // Check for duplicate sort positions
  const sortPositions = updates.map(update => update.newSortPosition)
  const uniqueSortPositions = new Set(sortPositions)
  if (uniqueSortPositions.size !== updates.length) return false

  // Check for gaps in sort positions
  const min = Math.min(...sortPositions)
  const max = Math.max(...sortPositions)
  if (min !== 1 || max !== updates.length) return false

  // Check that all required fields are present
  return updates.every(update => 
    update.modifierId && 
    update.customizationId && 
    typeof update.newSortPosition === 'number' &&
    typeof update.price === 'number'
  )
}

/**
 * Handle reordering of modifiers by updating their sort positions
 * @param newModifiers - Array of modifiers in their new order
 * @param customizationId - ID of the parent customization group
 * @returns Promise that resolves when the reordering is complete
 */
export const handleModifiersReorder = async (
  newModifiers: FormattedMenuSectionInfo[],
  customizationId: string
): Promise<void> => {
  try {
    // Get the customization_customization_options for this customization group
    const existingOptions = await getMenuMappingCustomizationCustomizationOptions({ 
      customizationId 
    })

    // Create a map of modifier IDs to their customization_customization_option IDs
    const modifierToCustomizationOptionMap = existingOptions.reduce((acc: Record<string, string>, option: MenuMappingCustomizationCustomizationOption) => {
      const modifierId = option.relationships.customization_option.data.id
      acc[modifierId] = option.id
      return acc
    }, {} as Record<string, string>)

    // Calculate new sort positions
    const modifierUpdates = calculateModifierSortPositions(newModifiers, customizationId)

    // Map the updates to use the correct customization_customization_option IDs
    const mappedUpdates = modifierUpdates.map(update => ({
      ...update,
      modifierId: modifierToCustomizationOptionMap[update.modifierId] // Use the correct ID
    }))

    // Validate the sort positions
    if (!validateModifierSortPositions(mappedUpdates)) {
      throw new Error('Invalid sort positions calculated')
    }

    // Perform bulk update
    await bulkUpdateCustomizationOptionsSortPositions(mappedUpdates)
  } catch (error) {
    console.error('Error updating modifier order:', error)
    throw error
  }
}

/**
 * Update local state for modifier reordering (for immediate UI feedback)
 * @param sectionItems - Current section items state
 * @param customizationId - ID of the customization group being reordered
 * @param newModifiers - Array of modifiers in their new order
 * @returns Updated section items with new modifier order
 */
export const updateModifiersLocalState = (
  sectionItems: FormattedMenuSectionInfo[],
  customizationId: string,
  newModifiers: FormattedMenuSectionInfo[]
): FormattedMenuSectionInfo[] => {
  return sectionItems.map(item => {
    // Check if this item has the customization group
    const hasCustomization = item.children.customizations?.some(customization => customization.id === customizationId)
    if (hasCustomization) {
      return {
        ...item,
        children: {
          ...item.children,
          customizations: item.children.customizations?.map(customization =>
            customization.id === customizationId
              ? { ...customization, children: { ...customization.children, customization_options: newModifiers } }
              : customization
          )
        }
      }
    }

    // Check if this item has child items with the customization group
    const hasChildWithCustomization = item.children.child_items?.some(childItem =>
      childItem.children.customizations?.some(customization => customization.id === customizationId)
    )
    if (hasChildWithCustomization) {
      return {
        ...item,
        children: {
          ...item.children,
          child_items: item.children.child_items?.map(childItem => {
            const hasCustomization = childItem.children.customizations?.some(customization => customization.id === customizationId)
            if (hasCustomization) {
              return {
                ...childItem,
                children: {
                  ...childItem.children,
                  customizations: childItem.children.customizations?.map(customization =>
                    customization.id === customizationId
                      ? { ...customization, children: { ...customization.children, customization_options: newModifiers } }
                      : customization
                  )
                }
              }
            }
            return childItem
          })
        }
      }
    }

    return item
  })
}

/**
 * Handle items reordering from drag and drop
 * Updates the formattedMenuSection state with new items order
 * @param formattedMenuSection - Current menu section state
 * @param subsectionId - ID of the subsection to update
 * @param newItems - Array of items in their new order
 * @param onItemsReorder - Optional parent callback for reordering
 * @returns Updated menu section or undefined if no changes
 */
export const handleItemsReorder = (
  formattedMenuSection: FormattedMenuSectionInfo | undefined,
  subsectionId: string,
  newItems: FormattedMenuSectionInfo[],
  onItemsReorder?: (subsectionId: string, newItems: FormattedMenuSectionInfo[]) => void
): FormattedMenuSectionInfo | undefined => {
  if (onItemsReorder) {
    onItemsReorder(subsectionId, newItems)
    return undefined
  }

  if (formattedMenuSection?.children?.subsections) {
    const updatedSubsections = formattedMenuSection.children.subsections.map(subsection => {
      if (subsection.id === subsectionId) {
        return {
          ...subsection,
          children: {
            ...subsection.children,
            items: newItems
          }
        }
      }
      return subsection
    })

    return {
      ...formattedMenuSection,
      children: {
        ...formattedMenuSection.children,
        subsections: updatedSubsections
      }
    }
  }

  return undefined
}

/**
 * Handle child items reordering from drag and drop
 * Updates the formattedMenuSection state with new child items order
 * @param formattedMenuSection - Current menu section state
 * @param subsectionId - ID of the subsection containing the parent item
 * @param parentItemId - ID of the parent item containing the child items
 * @param newChildItems - Array of child items in their new order
 * @param onChildItemsReorder - Optional parent callback for reordering
 * @returns Updated menu section or undefined if no changes
 */
export const handleChildItemsReorder = (
  formattedMenuSection: FormattedMenuSectionInfo | undefined,
  subsectionId: string,
  parentItemId: string,
  newChildItems: FormattedMenuSectionInfo[],
  onChildItemsReorder?: (subsectionId: string, parentItemId: string, newChildItems: FormattedMenuSectionInfo[]) => void
): FormattedMenuSectionInfo | undefined => {
  if (onChildItemsReorder) {
    onChildItemsReorder(subsectionId, parentItemId, newChildItems)
    return undefined
  }

  if (formattedMenuSection?.children?.subsections) {
    const updatedSubsections = formattedMenuSection.children.subsections.map(subsection => {
      if (subsection.id === subsectionId) {
        const updatedItems = subsection.children.items?.map(item => {
          if (item.id === parentItemId) {
            return {
              ...item,
              children: {
                ...item.children,
                child_items: newChildItems
              }
            }
          }
          return item
        }) || []

        return {
          ...subsection,
          children: {
            ...subsection.children,
            items: updatedItems
          }
        }
      }
      return subsection
    })

    return {
      ...formattedMenuSection,
      children: {
        ...formattedMenuSection.children,
        subsections: updatedSubsections
      }
    }
  }

  return undefined
}

/**
 * Handle modifiers reordering from drag and drop
 * Updates the formattedMenuSection state with new modifier order
 * @param formattedMenuSection - Current menu section state
 * @param customizationId - ID of the customization group being reordered
 * @param newModifiers - Array of modifiers in their new order
 * @param onModifiersReorder - Optional parent callback for reordering
 * @returns Updated menu section or undefined if no changes
 */
export const handleModifiersReorderForMenuSection = (
  formattedMenuSection: FormattedMenuSectionInfo | undefined,
  customizationId: string,
  newModifiers: FormattedMenuSectionInfo[],
  onModifiersReorder?: (customizationId: string, newModifiers: FormattedMenuSectionInfo[]) => void
): FormattedMenuSectionInfo | undefined => {
  if (onModifiersReorder) {
    onModifiersReorder(customizationId, newModifiers)
    return undefined
  }

  if (formattedMenuSection?.children?.subsections) {
    const updatedSubsections = formattedMenuSection.children.subsections.map(subsection => {
      const updatedItems = subsection.children.items?.map(item => {
        // Check if this item has the customization group
        const hasCustomization = item.children.customizations?.some(customization => customization.id === customizationId)
        if (hasCustomization) {
          return {
            ...item,
            children: {
              ...item.children,
              customizations: item.children.customizations?.map(customization =>
                customization.id === customizationId
                  ? { ...customization, children: { ...customization.children, customization_options: newModifiers } }
                  : customization
              )
            }
          }
        }

        // Check if this item has child items with the customization group
        const hasChildWithCustomization = item.children.child_items?.some(childItem =>
          childItem.children.customizations?.some(customization => customization.id === customizationId)
        )
        if (hasChildWithCustomization) {
          return {
            ...item,
            children: {
              ...item.children,
              child_items: item.children.child_items?.map(childItem => {
                const hasCustomization = childItem.children.customizations?.some(customization => customization.id === customizationId)
                if (hasCustomization) {
                  return {
                    ...childItem,
                    children: {
                      ...childItem.children,
                      customizations: childItem.children.customizations?.map(customization =>
                        customization.id === customizationId
                          ? { ...customization, children: { ...customization.children, customization_options: newModifiers } }
                          : customization
                      )
                    }
                  }
                }
                return childItem
              })
            }
          }
        }

        return item
      }) || []

      return {
        ...subsection,
        children: {
          ...subsection.children,
          items: updatedItems
        }
      }
    })

    return {
      ...formattedMenuSection,
      children: {
        ...formattedMenuSection.children,
        subsections: updatedSubsections
      }
    }
  }

  return undefined
}

/**
 * Handle reordering of nested modifiers by updating their sort positions
 * @param newNestedModifiers - Array of nested modifiers in their new order
 * @param parentOptionId - ID of the parent modifier option
 * @param customizationId - ID of the nested customization group
 * @returns Promise that resolves when the reordering is complete
 */
export const handleNestedModifiersReorder = async (
  nestedModifiers: ItemModifier[],
  parentOptionId: string,
  customizationId: string,
  onSuccess?: () => void,
  onError?: (error: any) => void
) => {
  try {
    // Get the customization_customization_options for this nested customization group
    const existingOptions = await getMenuMappingCustomizationCustomizationOptions({ 
      customizationId 
    })

    // Create a map of modifier IDs to their customization_customization_option IDs
    const modifierToCustomizationOptionMap = existingOptions.reduce((acc: Record<string, string>, option: MenuMappingCustomizationCustomizationOption) => {
      const modifierId = option.relationships.customization_option.data.id
      acc[modifierId] = option.id
      return acc
    }, {} as Record<string, string>)

    // Calculate new sort positions for nested modifiers
    const nestedModifierUpdates = nestedModifiers.map((modifier, index) => ({
      modifierId: modifier.id,
      customizationId,
      newSortPosition: index + 1,
      price: modifier.attributes.price || 0
    }))

    // Map the updates to use the correct customization_customization_option IDs
    const mappedUpdates = nestedModifierUpdates.map(update => ({
      ...update,
      modifierId: modifierToCustomizationOptionMap[update.modifierId] // Use the correct ID
    }))

    // Validate the sort positions
    if (!validateModifierSortPositions(mappedUpdates)) {
      throw new Error('Invalid sort positions calculated for nested modifiers')
    }

    // Perform bulk update using the same logic as parent modifiers
    await bulkUpdateCustomizationOptionsSortPositions(mappedUpdates)
    onSuccess?.()
  } catch (error) {
    console.error('Failed to update nested modifier sort positions:', error)
    onError?.(error)
  }
}

/**
 * Convert FormattedMenuSectionInfo[] to ItemModifier[] for API calls
 * @param modifiers - Array of FormattedMenuSectionInfo modifiers
 * @returns Array of ItemModifier objects
 */
export const convertToItemModifiers = (modifiers: FormattedMenuSectionInfo[]): ItemModifier[] => {
  return modifiers.map(modifier => ({
    id: modifier.id,
    type: modifier.type,
    attributes: {
      external_id: modifier.id,
      suspend_until: modifier.attributes.suspend_until || null,
      title: modifier.attributes.title || '',
      price: modifier.attributes.price,
      sort_position: modifier.attributes.sort_position
    },
    children: {
      customizations: modifier.children.customizations
    }
  })) as ItemModifier[]
}

/**
 * Update local state for nested modifier reordering (for immediate UI feedback)
 * @param groupModifiers - Current group modifiers state
 * @param parentModifierId - ID of the parent modifier containing the nested modifiers
 * @param nestedCustomizationId - ID of the nested customization group being reordered
 * @param newNestedModifiers - Array of nested modifiers in their new order
 * @returns Updated group modifiers with new nested modifier order
 */
export const updateNestedModifiersLocalState = (
  groupModifiers: FormattedMenuSectionInfo[],
  parentModifierId: string,
  nestedCustomizationId: string,
  newNestedModifiers: FormattedMenuSectionInfo[]
): FormattedMenuSectionInfo[] => {
  return groupModifiers.map(modifier => {
    if (modifier.id === parentModifierId) {
      return {
        ...modifier,
        children: {
          ...modifier.children,
          customizations: modifier.children.customizations?.map(customization => 
            customization.id === nestedCustomizationId 
              ? { ...customization, children: { ...customization.children, customization_options: newNestedModifiers } }
              : customization
          )
        }
      }
    }
    return modifier
  })
}

/**
 * Update local state for modifier reordering in MenuSubsection component
 * @param itemsToDisplay - Current items state
 * @param customizationId - ID of the customization group being reordered
 * @param newModifiers - Array of modifiers in their new order
 * @returns Updated items with new modifier order
 */
export const updateModifiersLocalStateForMenuSubsection = (
  itemsToDisplay: FormattedMenuSectionInfo[],
  customizationId: string,
  newModifiers: FormattedMenuSectionInfo[]
): FormattedMenuSectionInfo[] => {
  return itemsToDisplay.map(item => {
    // Check if this item has the customization group
    const hasCustomization = item.children.customizations?.some(customization => customization.id === customizationId)
    if (hasCustomization) {
      return {
        ...item,
        children: {
          ...item.children,
          customizations: item.children.customizations?.map(customization =>
            customization.id === customizationId
              ? { ...customization, children: { ...customization.children, customization_options: newModifiers } }
              : customization
          )
        }
      }
    }

    // Check if this item has child items with the customization group
    const hasChildWithCustomization = item.children.child_items?.some(childItem =>
      childItem.children.customizations?.some(customization => customization.id === customizationId)
    )
    if (hasChildWithCustomization) {
      return {
        ...item,
        children: {
          ...item.children,
          child_items: item.children.child_items?.map(childItem => {
            const hasCustomization = childItem.children.customizations?.some(customization => customization.id === customizationId)
            if (hasCustomization) {
              return {
                ...childItem,
                children: {
                  ...childItem.children,
                  customizations: childItem.children.customizations?.map(customization =>
                    customization.id === customizationId
                      ? { ...customization, children: { ...customization.children, customization_options: newModifiers } }
                      : customization
                  )
                }
              }
            }
            return childItem
          })
        }
      }
    }

    return item
  })
}

/**
 * Update local state for nested modifier reordering in MenuSubsection component
 * @param itemsToDisplay - Current items state
 * @param parentModifierId - ID of the parent modifier containing the nested modifiers
 * @param nestedCustomizationId - ID of the nested customization group being reordered
 * @param newNestedModifiers - Array of nested modifiers in their new order
 * @returns Updated items with new nested modifier order
 */
export const updateNestedModifiersLocalStateForMenuSubsection = (
  itemsToDisplay: FormattedMenuSectionInfo[],
  parentModifierId: string,
  nestedCustomizationId: string,
  newNestedModifiers: FormattedMenuSectionInfo[]
): FormattedMenuSectionInfo[] => {
  return itemsToDisplay.map(item => {
    // Check if this item has the parent modifier with nested modifiers
    const hasParentModifier = item.children.customizations?.some(customization => 
      customization.children.customization_options?.some(modifier => modifier.id === parentModifierId)
    )
    
    if (hasParentModifier) {
      return {
        ...item,
        children: {
          ...item.children,
          customizations: item.children.customizations?.map(customization => {
            const hasParentModifier = customization.children.customization_options?.some(modifier => modifier.id === parentModifierId)
            if (hasParentModifier) {
              return {
                ...customization,
                children: {
                  ...customization.children,
                  customization_options: customization.children.customization_options?.map(modifier => {
                    if (modifier.id === parentModifierId) {
                      return {
                        ...modifier,
                        children: {
                          ...modifier.children,
                          customizations: modifier.children.customizations?.map(nestedCustomization =>
                            nestedCustomization.id === nestedCustomizationId
                              ? { ...nestedCustomization, children: { ...nestedCustomization.children, customization_options: newNestedModifiers } }
                              : nestedCustomization
                          )
                        }
                      }
                    }
                    return modifier
                  })
                }
              }
            }
            return customization
          })
        }
      }
    }

    // Check if this item has child items with the parent modifier
    const hasChildWithParentModifier = item.children.child_items?.some(childItem =>
      childItem.children.customizations?.some(customization => 
        customization.children.customization_options?.some(modifier => modifier.id === parentModifierId)
      )
    )
    
    if (hasChildWithParentModifier) {
      return {
        ...item,
        children: {
          ...item.children,
          child_items: item.children.child_items?.map(childItem => {
            const hasParentModifier = childItem.children.customizations?.some(customization => 
              customization.children.customization_options?.some(modifier => modifier.id === parentModifierId)
            )
            if (hasParentModifier) {
              return {
                ...childItem,
                children: {
                  ...childItem.children,
                  customizations: childItem.children.customizations?.map(customization => {
                    const hasParentModifier = customization.children.customization_options?.some(modifier => modifier.id === parentModifierId)
                    if (hasParentModifier) {
                      return {
                        ...customization,
                        children: {
                          ...customization.children,
                          customization_options: customization.children.customization_options?.map(modifier => {
                            if (modifier.id === parentModifierId) {
                              return {
                                ...modifier,
                                children: {
                                  ...modifier.children,
                                  customizations: modifier.children.customizations?.map(nestedCustomization =>
                                    nestedCustomization.id === nestedCustomizationId
                                      ? { ...nestedCustomization, children: { ...nestedCustomization.children, customization_options: newNestedModifiers } }
                                      : nestedCustomization
                                  )
                                }
                              }
                            }
                            return modifier
                          })
                        }
                      }
                    }
                    return customization
                  })
                }
              }
            }
            return childItem
          })
        }
      }
    }

    return item
  })
}

/**
 * Update a single item in the formattedMenuSection
 * @param formattedMenuSection - Current menu section state
 * @param updatedItem - The updated item to replace the existing one
 * @returns Updated menu section or undefined if no changes
 */
export const updateItemInFormattedMenuSection = (
  formattedMenuSection: FormattedMenuSectionInfo | undefined,
  updatedItem: FormattedMenuSectionInfo
): FormattedMenuSectionInfo | undefined => {
  if (!formattedMenuSection?.children?.subsections) {
    return undefined
  }

  const updatedSubsections = formattedMenuSection.children.subsections.map(subsection => ({
    ...subsection,
    children: {
      ...subsection.children,
      items: subsection.children.items?.map(item => 
        item.id === updatedItem.id ? updatedItem : item
      )
    }
  }))

  return {
    ...formattedMenuSection,
    children: {
      ...formattedMenuSection.children,
      subsections: updatedSubsections
    }
  }
}

/**
 * Handle customization groups reordering with backend API call
 * @param newCustomizationGroups - Array of customization groups in their new order
 */
export const handleCustomizationGroupsReorder = async (
  newCustomizationGroups: FormattedMenuSectionInfo[]
): Promise<void> => {
  try {
    await Promise.all(
      newCustomizationGroups.map((group, index) => {
        const joinRecordId = group.relationships?.item_customizations?.data?.[0]?.id
        if (!joinRecordId) return Promise.resolve()
        return updateMenuMappingItemCustomizationSortPosition({
          id: joinRecordId,
          customization_sort_position: index + 1,
        })
      })
    )
  } catch (error) {
    console.error('Error updating customization group order:', error)
    throw error
  }
}

/**
 * Update local state for customization groups reordering
 * @param itemsToDisplay - Current items state
 * @param itemId - ID of the parent item containing the customization groups
 * @param newCustomizationGroups - Array of customization groups in their new order
 * @returns Updated items with new customization group order
 */
export const updateCustomizationGroupsLocalState = (
  itemsToDisplay: FormattedMenuSectionInfo[],
  itemId: string,
  newCustomizationGroups: FormattedMenuSectionInfo[]
): FormattedMenuSectionInfo[] => {
  return itemsToDisplay.map(item => {
    if (item.id === itemId) {
      return {
        ...item,
        children: {
          ...item.children,
          customizations: newCustomizationGroups
        }
      }
    }

    // Check if this item has child items with the customization groups
    const hasChildWithCustomizations = item.children.child_items?.some(childItem => childItem.id === itemId)
    if (hasChildWithCustomizations) {
      return {
        ...item,
        children: {
          ...item.children,
          child_items: item.children.child_items?.map(childItem => 
            childItem.id === itemId
              ? { ...childItem, children: { ...childItem.children, customizations: newCustomizationGroups } }
              : childItem
          )
        }
      }
    }

    return item
  })
}

/**
 * Handle customization groups reordering from drag and drop
 * Updates the formattedMenuSection state with new customization groups order
 * @param formattedMenuSection - Current menu section state
 * @param itemId - ID of the item containing the customization groups
 * @param newCustomizationGroups - Array of customization groups in their new order
 * @param onCustomizationGroupsReorder - Optional parent callback for reordering
 * @returns Updated menu section or undefined if no changes
 */
export const handleCustomizationGroupsReorderForMenuSection = (
  formattedMenuSection: FormattedMenuSectionInfo | undefined,
  itemId: string,
  newCustomizationGroups: FormattedMenuSectionInfo[],
  onCustomizationGroupsReorder?: (itemId: string, newCustomizationGroups: FormattedMenuSectionInfo[]) => void
): FormattedMenuSectionInfo | undefined => {
  if (onCustomizationGroupsReorder) {
    onCustomizationGroupsReorder(itemId, newCustomizationGroups)
    return undefined
  }

  if (formattedMenuSection?.children?.subsections) {
    const updatedSubsections = formattedMenuSection.children.subsections.map(subsection => {
      const updatedItems = subsection.children.items?.map(item => {
        if (item.id === itemId) {
          return {
            ...item,
            children: {
              ...item.children,
              customizations: newCustomizationGroups
            }
          }
        }

        // Check if this item has child items with the customization groups
        const hasChildWithCustomizations = item.children.child_items?.some(childItem => childItem.id === itemId)
        if (hasChildWithCustomizations) {
          return {
            ...item,
            children: {
              ...item.children,
              child_items: item.children.child_items?.map(childItem => 
                childItem.id === itemId
                  ? { ...childItem, children: { ...childItem.children, customizations: newCustomizationGroups } }
                  : childItem
              )
            }
          }
        }

        return item
      }) || []

      return {
        ...subsection,
        children: {
          ...subsection.children,
          items: updatedItems
        }
      }
    })

    return {
      ...formattedMenuSection,
      children: {
        ...formattedMenuSection.children,
        subsections: updatedSubsections
      }
    }
  }

  return undefined
}

/**
 * Assign sort positions to modifiers based on their order
 * @param modifiers - Array of modifiers in their new order
 * @returns Array of modifiers with sort_position assigned
 */
export const assignSortPositionsToModifiers = (
  modifiers: FormattedMenuSectionInfo[]
): FormattedMenuSectionInfo[] => {
  return modifiers.map((modifier, index) => ({
    ...modifier,
    attributes: {
      ...modifier.attributes,
      sort_position: index + 1
    }
  }))
}

/**
 * Extract all items from all subsections in a menu section
 * @param menuSection - The menu section containing subsections
 * @returns Array of all items from all subsections
 */
export const extractAllItemsFromSubsections = (
  menuSection: FormattedMenuSectionInfo | undefined
): FormattedMenuSectionInfo[] => {
  return menuSection?.children?.subsections?.flatMap(subsection => 
    subsection.children?.items || []
  ) || []
}

/**
 * Update subsections with modified items
 * @param subsections - Array of subsections to update
 * @param updatedItems - Array of updated items
 * @returns Array of subsections with updated items
 */
export const updateSubsectionsWithItems = (
  subsections: FormattedMenuSectionInfo[] | undefined,
  updatedItems: FormattedMenuSectionInfo[]
): FormattedMenuSectionInfo[] => {
  return subsections?.map(subsection => ({
    ...subsection,
    children: {
      ...subsection.children,
      items: subsection.children.items?.map(item => {
        const updatedItem = updatedItems.find(updated => updated.id === item.id)
        return updatedItem || item
      })
    }
  })) || []
}

/**
 * Update local state for modifier reordering in modal (direct customization groups)
 * @param customizationGroups - Current customization groups state
 * @param customizationId - ID of the customization group being reordered
 * @param newModifiers - Array of modifiers in their new order
 * @returns Updated customization groups with new modifier order
 */
export const updateModifiersLocalStateForModal = (
  customizationGroups: FormattedMenuSectionInfo[],
  customizationId: string,
  newModifiers: FormattedMenuSectionInfo[]
): FormattedMenuSectionInfo[] => {
  return customizationGroups.map((customization) => {
    if (customization.id === customizationId) {
      return {
        ...customization,
        children: {
          ...customization.children,
          customization_options: newModifiers,
        },
      }
    }
    return customization
  })
}

/**
 * Update local state for nested modifier reordering in modal (direct customization groups)
 * @param customizationGroups - Current customization groups state
 * @param parentModifierId - ID of the parent modifier containing the nested modifiers
 * @param nestedCustomizationId - ID of the nested customization group being reordered
 * @param newNestedModifiers - Array of nested modifiers in their new order
 * @returns Updated customization groups with new nested modifier order
 */
export const updateNestedModifiersLocalStateForModal = (
  customizationGroups: FormattedMenuSectionInfo[],
  parentModifierId: string,
  nestedCustomizationId: string,
  newNestedModifiers: FormattedMenuSectionInfo[]
): FormattedMenuSectionInfo[] => {
  return customizationGroups.map((customization) => {
    // Find the parent modifier that contains the nested modifiers
    const parentModifier = customization.children.customization_options?.find(
      (modifier) => modifier.id === parentModifierId
    )
    
    if (parentModifier) {
      // Update the nested customization within the parent modifier
      const updatedParentModifier = {
        ...parentModifier,
        children: {
          ...parentModifier.children,
          customizations: parentModifier.children.customizations?.map((nestedCustomization) => {
            if (nestedCustomization.id === nestedCustomizationId) {
              return {
                ...nestedCustomization,
                children: {
                  ...nestedCustomization.children,
                  customization_options: newNestedModifiers,
                },
              }
            }
            return nestedCustomization
          }),
        },
      }

      // Update the parent modifier in the customization
      return {
        ...customization,
        children: {
          ...customization.children,
          customization_options: customization.children.customization_options?.map((modifier) =>
            modifier.id === parentModifierId ? updatedParentModifier : modifier
          ),
        },
      }
    }
    return customization
  })
}

/**
 * Update local state for nested customization groups reordering in modal
 * @param customizationGroups - Current customization groups state
 * @param parentModifierId - ID of the parent modifier containing the nested customization groups
 * @param newNestedCustomizationGroups - Array of nested customization groups in their new order
 * @returns Updated customization groups with new nested customization groups order
 */
export const updateNestedCustomizationGroupsLocalStateForModal = (
  customizationGroups: FormattedMenuSectionInfo[],
  parentModifierId: string,
  newNestedCustomizationGroups: FormattedMenuSectionInfo[]
): FormattedMenuSectionInfo[] => {
  return customizationGroups.map((customization) => {
    // Find the parent modifier that contains the nested customization groups
    const parentModifier = customization.children.customization_options?.find(
      (modifier) => modifier.id === parentModifierId
    )
    
    if (parentModifier) {
      // Update the parent modifier with new nested customization groups
      const updatedParentModifier = {
        ...parentModifier,
        children: {
          ...parentModifier.children,
          customizations: newNestedCustomizationGroups,
        },
      }

      // Update the parent modifier in the customization
      return {
        ...customization,
        children: {
          ...customization.children,
          customization_options: customization.children.customization_options?.map((modifier) =>
            modifier.id === parentModifierId ? updatedParentModifier : modifier
          ),
        },
      }
    }
    return customization
  })
}

/**
 * Update local state for nested customization groups reordering in menu subsection
 * @param itemsToDisplay - Current items state
 * @param parentModifierId - ID of the parent modifier containing the nested customization groups
 * @param newNestedCustomizationGroups - Array of nested customization groups in their new order
 * @returns Updated items with new nested customization groups order
 */
export const updateNestedCustomizationGroupsLocalStateForMenuSubsection = (
  itemsToDisplay: FormattedMenuSectionInfo[],
  parentModifierId: string,
  newNestedCustomizationGroups: FormattedMenuSectionInfo[]
): FormattedMenuSectionInfo[] => {
  return itemsToDisplay.map(item => {
    // Check if this item has the parent modifier with nested customization groups
    const hasParentModifier = item.children.customizations?.some(customization =>
      customization.children.customization_options?.some(modifier => modifier.id === parentModifierId)
    )
    
    if (hasParentModifier) {
      return {
        ...item,
        children: {
          ...item.children,
          customizations: item.children.customizations?.map(customization => {
            // Find the parent modifier in this customization
            const parentModifier = customization.children.customization_options?.find(
              modifier => modifier.id === parentModifierId
            )
            
            if (parentModifier) {
              // Update the parent modifier with new nested customization groups
              const updatedParentModifier = {
                ...parentModifier,
                children: {
                  ...parentModifier.children,
                  customizations: newNestedCustomizationGroups,
                },
              }

              // Update the parent modifier in the customization
              return {
                ...customization,
                children: {
                  ...customization.children,
                  customization_options: customization.children.customization_options?.map(modifier =>
                    modifier.id === parentModifierId ? updatedParentModifier : modifier
                  ),
                },
              }
            }
            return customization
          })
        }
      }
    }

    // Check if this item has child items with the parent modifier
    const hasChildWithParentModifier = item.children.child_items?.some(childItem =>
      childItem.children.customizations?.some(customization =>
        customization.children.customization_options?.some(modifier => modifier.id === parentModifierId)
      )
    )
    
    if (hasChildWithParentModifier) {
      return {
        ...item,
        children: {
          ...item.children,
          child_items: item.children.child_items?.map(childItem => {
            const hasParentModifier = childItem.children.customizations?.some(customization =>
              customization.children.customization_options?.some(modifier => modifier.id === parentModifierId)
            )
            
            if (hasParentModifier) {
              return {
                ...childItem,
                children: {
                  ...childItem.children,
                  customizations: childItem.children.customizations?.map(customization => {
                    // Find the parent modifier in this customization
                    const parentModifier = customization.children.customization_options?.find(
                      modifier => modifier.id === parentModifierId
                    )
                    
                    if (parentModifier) {
                      // Update the parent modifier with new nested customization groups
                      const updatedParentModifier = {
                        ...parentModifier,
                        children: {
                          ...parentModifier.children,
                          customizations: newNestedCustomizationGroups,
                        },
                      }

                      // Update the parent modifier in the customization
                      return {
                        ...customization,
                        children: {
                          ...customization.children,
                          customization_options: customization.children.customization_options?.map(modifier =>
                            modifier.id === parentModifierId ? updatedParentModifier : modifier
                          ),
                        },
                      }
                    }
                    return customization
                  })
                }
              }
            }
            return childItem
          })
        }
      }
    }

    return item
  })
}

/**
 * Handle nested customization groups reordering from drag and drop
 * Updates the formattedMenuSection state with new nested customization groups order
 * @param formattedMenuSection - Current menu section state
 * @param parentModifierId - ID of the parent modifier containing the nested customization groups
 * @param newNestedCustomizationGroups - Array of nested customization groups in their new order
 * @param onNestedCustomizationGroupsReorder - Optional parent callback for reordering
 * @returns Updated menu section or undefined if no changes
 */
export const handleNestedCustomizationGroupsReorderForMenuSection = (
  formattedMenuSection: FormattedMenuSectionInfo | undefined,
  parentModifierId: string,
  newNestedCustomizationGroups: FormattedMenuSectionInfo[],
  onNestedCustomizationGroupsReorder?: (parentModifierId: string, newNestedCustomizationGroups: FormattedMenuSectionInfo[]) => void
): FormattedMenuSectionInfo | undefined => {
  if (onNestedCustomizationGroupsReorder) {
    onNestedCustomizationGroupsReorder(parentModifierId, newNestedCustomizationGroups)
    return undefined
  }

  if (formattedMenuSection?.children?.subsections) {
    const updatedSubsections = formattedMenuSection.children.subsections.map(subsection => {
      const updatedItems = subsection.children.items?.map(item => {
        // Check if this item has the parent modifier with nested customization groups
        const hasParentModifier = item.children.customizations?.some(customization =>
          customization.children.customization_options?.some(modifier => modifier.id === parentModifierId)
        )
        
        if (hasParentModifier) {
          return {
            ...item,
            children: {
              ...item.children,
              customizations: item.children.customizations?.map(customization => {
                // Find the parent modifier in this customization
                const parentModifier = customization.children.customization_options?.find(
                  modifier => modifier.id === parentModifierId
                )
                
                if (parentModifier) {
                  // Update the parent modifier with new nested customization groups
                  const updatedParentModifier = {
                    ...parentModifier,
                    children: {
                      ...parentModifier.children,
                      customizations: newNestedCustomizationGroups,
                    },
                  }

                  // Update the parent modifier in the customization
                  return {
                    ...customization,
                    children: {
                      ...customization.children,
                      customization_options: customization.children.customization_options?.map(modifier =>
                        modifier.id === parentModifierId ? updatedParentModifier : modifier
                      ),
                    },
                  }
                }
                return customization
              })
            }
          }
        }

        // Check if this item has child items with the parent modifier
        const hasChildWithParentModifier = item.children.child_items?.some(childItem =>
          childItem.children.customizations?.some(customization =>
            customization.children.customization_options?.some(modifier => modifier.id === parentModifierId)
          )
        )
        
        if (hasChildWithParentModifier) {
          return {
            ...item,
            children: {
              ...item.children,
              child_items: item.children.child_items?.map(childItem => {
                const hasParentModifier = childItem.children.customizations?.some(customization =>
                  customization.children.customization_options?.some(modifier => modifier.id === parentModifierId)
                )
                
                if (hasParentModifier) {
                  return {
                    ...childItem,
                    children: {
                      ...childItem.children,
                      customizations: childItem.children.customizations?.map(customization => {
                        // Find the parent modifier in this customization
                        const parentModifier = customization.children.customization_options?.find(
                          modifier => modifier.id === parentModifierId
                        )
                        
                        if (parentModifier) {
                          // Update the parent modifier with new nested customization groups
                          const updatedParentModifier = {
                            ...parentModifier,
                            children: {
                              ...parentModifier.children,
                              customizations: newNestedCustomizationGroups,
                            },
                          }

                          // Update the parent modifier in the customization
                          return {
                            ...customization,
                            children: {
                              ...customization.children,
                              customization_options: customization.children.customization_options?.map(modifier =>
                                modifier.id === parentModifierId ? updatedParentModifier : modifier
                              ),
                            },
                          }
                        }
                        return customization
                      })
                    }
                  }
                }
                return childItem
              })
            }
          }
        }

        return item
      }) || []

      return {
        ...subsection,
        children: {
          ...subsection.children,
          items: updatedItems
        }
      }
    })

    return {
      ...formattedMenuSection,
      children: {
        ...formattedMenuSection.children,
        subsections: updatedSubsections
      }
    }
  }

  return undefined
} 

/**
 * Handle nested customization groups reordering with backend API call
 * @param newNestedCustomizationGroups - Array of nested customization groups in their new order
 */
export const handleNestedCustomizationGroupsReorder = async (
  newNestedCustomizationGroups: FormattedMenuSectionInfo[]
): Promise<void> => {
  try {
    await Promise.all(
      newNestedCustomizationGroups.map(async (group, index) => {
        // For nested customization groups, the group.id is the nested customization record ID
        const nestedCustomizationId = group.id
        
        if (!nestedCustomizationId) return Promise.resolve()
        
        return updateMenuMappingNestedCustomization({
          id: nestedCustomizationId,
          attributes: {
            sort_position: index + 1,
          },
        })
      })
    )
  } catch (error) {
    console.error('Error updating nested customization group order:', error)
    throw error
  }
} 